import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { X } from "lucide-react";

export default function EditUserDialog({ user, onClose, onSave }) {
  const [membership, setMembership] = useState(user.membership_type || "foundation");
  const [expires, setExpires] = useState(user.membership_expires || "");
  const [role, setRole] = useState(user.role || "user");
  const [sessionRollover, setSessionRollover] = useState(Boolean(user.session_rollover));
  const [trainerSearch, setTrainerSearch] = useState("");
  const [assignedTrainerId, setAssignedTrainerId] = useState(user.assigned_trainer_id || "");
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const { data: trainers = [] } = useQuery({
    queryKey: ["trainers"],
    queryFn: () => base44.entities.Trainer.list(),
  });

  const assignedTrainer = trainers.find((t) => t.user_id === assignedTrainerId);

  const filtered = trainerSearch.trim()
    ? trainers.filter((t) => t.name?.toLowerCase().includes(trainerSearch.toLowerCase()) && t.is_active !== false)
    : trainers.filter((t) => t.is_active !== false);

  const handleSelectTrainer = (trainer) => {
    setAssignedTrainerId(trainer.user_id);
    setTrainerSearch("");
    setShowDropdown(false);
  };

  const handleClearTrainer = () => {
    setAssignedTrainerId("");
    setTrainerSearch("");
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="w-[calc(100vw-1rem)] max-w-md sm:max-w-md p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="p-3 rounded-lg bg-muted/40">
            <p className="font-semibold text-sm truncate">{user.full_name || "—"}</p>
            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
          </div>
          <div className="space-y-1.5">
            <Label>Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="trainer">Trainer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Membership Tier</Label>
            <Select value={membership} onValueChange={setMembership}>
              <SelectTrigger className="h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="foundation">Foundation</SelectItem>
                <SelectItem value="momentum">Momentum</SelectItem>
                <SelectItem value="strength">Strength</SelectItem>
                <SelectItem value="elite_coaching">Elite Coaching</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Membership Expiry</Label>
            <Input
              type="date"
              value={expires}
              onChange={(e) => setExpires(e.target.value)}
              className="h-11"
            />
          </div>

          {/* Trainer Assignment */}
          <div className="space-y-1.5">
            <Label>Assigned Trainer</Label>
            {assignedTrainer ? (
              <div className="flex items-center justify-between px-3 py-2 rounded-md border border-border bg-muted/40 text-sm">
                <div className="flex items-center gap-2">
                  {assignedTrainer.photo_url ? (
                    <img src={assignedTrainer.photo_url} className="w-6 h-6 rounded-full object-cover" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs font-bold">
                      {assignedTrainer.name?.[0]?.toUpperCase()}
                    </div>
                  )}
                  <span className="font-medium">{assignedTrainer.name}</span>
                  {assignedTrainer.specialization && (
                    <span className="text-xs text-muted-foreground">· {assignedTrainer.specialization}</span>
                  )}
                </div>
                <button onClick={handleClearTrainer} className="text-muted-foreground hover:text-destructive transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="relative" ref={dropdownRef}>
                <Input
                  placeholder="Search trainer by name..."
                  value={trainerSearch}
                  onChange={(e) => { setTrainerSearch(e.target.value); setShowDropdown(true); }}
                  onFocus={() => setShowDropdown(true)}
                  onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                />
                {showDropdown && filtered.length > 0 && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border border-border rounded-xl shadow-xl overflow-hidden max-h-48 overflow-y-auto">
                    {filtered.map((trainer) => (
                      <button
                        key={trainer.id}
                        onMouseDown={() => handleSelectTrainer(trainer)}
                        className="flex items-center gap-3 w-full px-3 py-2.5 text-sm hover:bg-muted/60 transition-colors text-left"
                      >
                        {trainer.photo_url ? (
                          <img src={trainer.photo_url} className="w-7 h-7 rounded-full object-cover shrink-0" />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs font-bold shrink-0">
                            {trainer.name?.[0]?.toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{trainer.name}</p>
                          {trainer.specialization && <p className="text-xs text-muted-foreground">{trainer.specialization}</p>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 p-3 rounded-lg border bg-muted/30">
            <div className="flex-1">
              <Label className="font-medium" htmlFor="rollover-toggle">Session rollover</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                When on, unused monthly session credits accumulate into future months.
                (Only affects Strength &amp; Elite Coaching — Momentum's intro session never resets.)
              </p>
            </div>
            <label className="flex items-center gap-2 self-start sm:self-center cursor-pointer">
              <input
                id="rollover-toggle"
                type="checkbox"
                checked={sessionRollover}
                onChange={(e) => setSessionRollover(e.target.checked)}
                className="w-5 h-5 rounded accent-primary shrink-0"
                data-testid="rollover-toggle"
              />
              <span className="text-sm sm:hidden">{sessionRollover ? "On" : "Off"}</span>
            </label>
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2 sticky bottom-0 bg-background pb-1">
            <Button variant="outline" onClick={onClose} className="h-11 sm:h-10">Cancel</Button>
            <Button
              className="h-11 sm:h-10"
              onClick={() => onSave({ membership_type: membership, membership_expires: expires, role, assigned_trainer_id: assignedTrainerId || null, session_rollover: sessionRollover })}
            >
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}